import { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Icon } from "../icons";
import { useAppStore } from "../../state/useAppStore";
import { apiPost } from "../../api/client";

interface Message {
  role: "user" | "assistant";
  text: string;
}

function surfaceSummary(pathname: string): string {
  if (pathname.includes("/agents")) {
    return "Watching agent fleet · I'll surface coverage gaps and binding health here.";
  }
  if (pathname.includes("/templates")) {
    return "Browsing the template catalog · I can highlight high-confidence matches for your archetype.";
  }
  if (pathname.includes("/plays/")) {
    return "Watching play workspace · step events, replans, and decisions appear here.";
  }
  if (pathname.includes("/inbox")) {
    return "Reading the roadmap inbox · I'll suggest plan-it candidates as initiatives land.";
  }
  if (pathname.includes("/operator")) {
    return "Following Hands Ops queue · ready to draft replies and action templates.";
  }
  if (pathname.includes("/dashboard")) {
    return "Watching the dashboard · I'll surface drift, replans, and KPI deltas as they happen.";
  }
  return "Co-pilot is quiet. Surface-aware briefings will appear here as you move through plays, decisions, and agents.";
}

function pageLabel(pathname: string): string {
  if (pathname.includes("/agents")) return "Agents page";
  if (pathname.includes("/templates")) return "Templates catalog";
  if (pathname.match(/\/plays\/[^/]+/)) return "Play workspace";
  if (pathname.includes("/inbox")) return "Roadmap inbox";
  if (pathname.includes("/operator")) return "Operator queue";
  if (pathname.includes("/dashboard")) return "Dashboard";
  return "Workspace";
}

function workspaceIdFromPath(pathname: string): string | null {
  const m = pathname.match(/\/workspaces\/([^/]+)/);
  return m ? m[1]! : null;
}

function entityIdFromPath(pathname: string): string | undefined {
  // /workspaces/:ws/plays/:playId
  const playMatch = pathname.match(/\/plays\/([^/?#]+)/);
  if (playMatch) return playMatch[1];
  // /workspaces/:ws/inbox/:entryId
  const inboxMatch = pathname.match(/\/inbox\/([^/?#]+)/);
  if (inboxMatch) return inboxMatch[1];
  return undefined;
}

export function RightRail() {
  const collapsed = useAppStore((s) => s.railCollapsed);
  const toggle = useAppStore((s) => s.toggleRail);
  const { pathname } = useLocation();
  const summary = surfaceSummary(pathname);
  const workspaceId = workspaceIdFromPath(pathname);
  const entityId = entityIdFromPath(pathname);

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const consumeRailDraft = useAppStore((s) => s.consumeRailDraft);

  // Chat history is workspace-scoped on the API side (the /xami/chat
  // route reads context from the URL's workspaceId). The UI was lagging
  // — old messages stayed visible after a workspace switch, mixing
  // contexts visually even though the next API call would correctly use
  // the new workspace. Reset on workspaceId change so the conversation
  // window follows the company the user is viewing. The input is kept
  // intentionally — a half-typed question is the user's, not the
  // workspace's.
  useEffect(() => {
    setMessages([]);
    setError(null);
  }, [workspaceId]);

  // When another page primes a message (e.g. Spotted's "Tell XAMI" button),
  // pull it into the input on the next render so the user just hits enter.
  useEffect(() => {
    const draft = consumeRailDraft();
    if (draft) {
      setInput(draft);
      // Focus + scroll so the seeded message is visible on mobile.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [consumeRailDraft, collapsed]);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages]);

  async function send() {
    const text = input.trim();
    if (!text || busy || !workspaceId) return;
    setInput("");
    setError(null);
    setBusy(true);
    const nextMessages: Message[] = [...messages, { role: "user", text }];
    setMessages(nextMessages);
    try {
      const history = nextMessages.slice(-10).map((m) => ({
        role: m.role,
        content: m.text,
      }));
      // Remove last user message from history — it goes as the main message
      history.pop();
      const r = await apiPost<{ reply: string }>(
        `/api/workspaces/${workspaceId}/xami/chat`,
        { message: text, pageContext: pageLabel(pathname), entityId, history },
      );
      setMessages((prev) => [...prev, { role: "assistant", text: r.reply }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  if (collapsed) {
    return (
      <aside className="rail rail-collapsed" data-testid="rightrail">
        <button
          type="button"
          className="rail-tab"
          onClick={toggle}
          aria-label="Expand Co-pilot rail"
          style={{ background: "transparent", border: 0, font: "inherit" }}
        >
          XAMI
        </button>
      </aside>
    );
  }

  return (
    <aside className="rail" data-testid="rightrail">
      <div className="rail-head">
        <div className="ai-avatar">X</div>
        <div>
          <div className="name">XAMI</div>
          <div className="status">
            <span className="pulse" />
            Watching · workspace
          </div>
        </div>
        <div className="right">
          <button
            type="button"
            className="x"
            aria-label="Collapse Co-pilot rail"
            onClick={toggle}
            style={{ background: "transparent", border: 0 }}
          >
            ×
          </button>
        </div>
      </div>
      <div className="rail-body" ref={bodyRef}>
        <div className="rail-msg" data-testid="rightrail-summary">
          <div className="ai-avatar">X</div>
          <div className="bubble">{summary}</div>
        </div>
        {messages.map((m, i) => (
          <div
            key={i}
            className="rail-msg"
            style={{ justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}
          >
            {m.role === "assistant" && <div className="ai-avatar">X</div>}
            <div
              className="bubble"
              style={
                m.role === "user"
                  ? { background: "var(--accent)", color: "#fff", marginLeft: "auto" }
                  : {}
              }
            >
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="rail-msg">
            <div className="ai-avatar">X</div>
            <div className="bubble" style={{ color: "var(--text-3)" }}>…</div>
          </div>
        )}
        {error && (
          <div style={{ padding: "4px 8px", fontSize: 11, color: "var(--danger)" }}>
            {error}
          </div>
        )}
      </div>
      <div className="rail-ask">
        <div className="sub">Ask XAMI · aware of what's on your screen</div>
        <div className="rail-input">
          <input
            ref={inputRef}
            placeholder="Ask XAMI…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            disabled={busy || !workspaceId}
          />
          <button
            type="button"
            className="send"
            aria-label="Send"
            onClick={() => void send()}
            disabled={busy || !input.trim() || !workspaceId}
          >
            <Icon name="send" className="ic-svg sm" />
          </button>
        </div>
      </div>
    </aside>
  );
}
